import { useCallback, useEffect, useRef, useState } from "react";
import { map } from "rxjs";
import ImperansReferenceService, {
  ImperansReferenceKind,
} from "../../Services/ImperansReference.service";
import {
  clinicOptions,
  franchiseOptions,
  IReferencePickerOption,
} from "./ImperansReference.rules";

export interface IImperansReferenceList {
  options: IReferencePickerOption[];
  loading: boolean;
  /** Imperans could not be reached; the step says so and offers Retry (story 6374). */
  unavailable: boolean;
  /** True once a list has actually arrived for the current country. */
  loaded: boolean;
  retry: () => void;
}

/**
 * One of the paired Imperans's reference lists for a country, fetched when the step opens and again
 * whenever the country changes (story 6372: "the list is re-fetched"). The service shares the read
 * across the session; Retry asks fresh.
 */
export const useImperansReferenceList = (
  kind: ImperansReferenceKind,
  country: string | null | undefined,
  enabled: boolean = true,
): IImperansReferenceList => {
  const [options, setOptions] = useState<IReferencePickerOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [unavailable, setUnavailable] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [attempt, setAttempt] = useState<number>(0);
  // Set by Retry and consumed by the next fetch, so only that fetch bypasses the session copy.
  const freshRef = useRef<boolean>(false);

  const normalisedCountry = `${country || ""}`.trim().toUpperCase();

  useEffect(() => {
    if (!enabled || !normalisedCountry) {
      setOptions([]);
      setLoaded(false);
      setUnavailable(false);
      setLoading(false);
      return undefined;
    }

    const fresh = freshRef.current;
    freshRef.current = false;
    setLoading(true);
    setUnavailable(false);

    const source =
      kind === "franchises"
        ? ImperansReferenceService.getFranchises(normalisedCountry, fresh).pipe(map(franchiseOptions))
        : ImperansReferenceService.getClinics(normalisedCountry, fresh).pipe(map(clinicOptions));

    const subscription = source.subscribe({
      next: (list) => {
        setOptions(list);
        setLoaded(true);
        setLoading(false);
      },
      error: () => {
        setOptions([]);
        setLoaded(false);
        setUnavailable(true);
        setLoading(false);
      },
    });
    return () => subscription.unsubscribe();
  }, [kind, normalisedCountry, enabled, attempt]);

  const retry = useCallback(() => {
    freshRef.current = true;
    setAttempt((current) => current + 1);
  }, []);

  return { options, loading, unavailable, loaded, retry };
};
